import { Dispatch, SetStateAction, useState } from "react"
import { XCircle, AlertTriangle } from "lucide-react"
import { Button } from "@components/common/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/common/dialog"
import { Label } from "@components/common/label"
import { Textarea } from "@components/common/textarea"
import { Card, CardContent, CardHeader, CardTitle } from "@components/common/card"
import { Badge } from "@components/common/badge"
import { Checkbox } from "@components/common/checkbox"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/common/table"
import { Alert, AlertDescription, AlertTitle } from "@components/common/alert"
import { DictType } from "@pma/types"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import useTranslator from "@pma/hooks/use-translator"
import { useMutation } from "@tanstack/react-query"
import { toast } from "@pma/hooks/use-toast"
import {
  BILL_APPROVE_ENDPOINT,
  FINANCIAL_SERVER_URL,
  SIGN_BILL_ENDPOINT,
} from "@pma/utils/api-routes"
import axios from "axios"
import { formatCurrency } from "@pma/utils/utils"
import { cn, getFinancialErrorMessage } from "@pma/utils/helpers"

interface BillDeclineDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedBills: Array<{ id: string; name: string; amount: string; project: string }>
  setSelectedItems: Dispatch<SetStateAction<Array<any>>>
  onDecline: CallableFunction
  bill: DictType
}

export function BulkDeclineDialog({
  open,
  onOpenChange,
  selectedBills,
  setSelectedItems,
  onDecline,
  bill,
}: BillDeclineDialogProps) {
  const [reason, setReason] = useState("")

  const [useDifferentReasons, setUseDifferentReasons] = useState(false)

  const [individualReasons, setIndividualReasons] = useState<Record<string, string>>({})

  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const allBillItems = bill?._bill_items

  const supervisorStage = bill?.status === "approved"

  const declinedBillItems =
    allBillItems?.filter((item: DictType) =>
      selectedBills.some((bill) => bill == item.boq_item_id),
    ) || []

  const handleIndividualReasonChange = (billId: string, newReason: string) => {
    setIndividualReasons((prev) => ({
      ...prev,
      [billId]: newReason,
    }))
  }

  const declineBill = async (billItems: DictType[]) => {
    const endpoint = supervisorStage ? SIGN_BILL_ENDPOINT : BILL_APPROVE_ENDPOINT
    const response = await axios.post(
      FINANCIAL_SERVER_URL + endpoint,
      {
        bill_id: bill?.id,
        items: billItems,
        finish_signing: supervisorStage ? false : undefined,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const handleBillDeclineMutation = useMutation({
    mutationKey: ["decline-bill-items"],
    mutationFn: declineBill,
    onSuccess: () => {
      onDecline()
      setSelectedItems([])
      resetForm()
      onOpenChange(false)
      toast({
        title: translate("Success"),
        description: translate("bill items declined successfully"),
        variant: "success",
      })
    },
    onError: (error: any) => {
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "decline bill", translate),
        variant: "destructive",
      })
    },
  })

  const handleDecline = () => {
    const declineData = declinedBillItems.map((bill: DictType) => ({
      bill_item_id: bill.id,
      amount: bill?.amount,
      declined: true,
      message_text: useDifferentReasons ? individualReasons[bill.id] || "" : reason,
    }))

    handleBillDeclineMutation.mutate(declineData)
  }

  const resetForm = () => {
    setReason("")

    setUseDifferentReasons(false)

    setIndividualReasons({})
  }

  const handleCancel = () => {
    resetForm()
    onOpenChange(false)
  }

  const isValidDecline = () => {
    if (useDifferentReasons) {
      return declinedBillItems.every((bill: DictType) => individualReasons[bill.id]?.trim())
    }
    return reason.trim()
  }

  const calculateTotals = () => {
    const originalTotal = declinedBillItems?.reduce((sum: any, bill: any) => {
      return (
        sum + Number.parseFloat(bill?.amount) * Number.parseFloat(bill?._boq_item?.discount_price)
      )
    }, 0)

    return { originalTotal }
  }

  const { originalTotal } = calculateTotals()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex w-full flex-col overflow-y-auto md:max-w-2xl lg:max-w-3xl xl:max-w-7xl h-fit md:h-fit",
          isHebrew ? "rtl" : "ltr",
        )}
      >
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <XCircle className='h-5 w-5 text-destructive' />
            {translate("Decline Bill items")}
          </DialogTitle>
          <DialogDescription>
            {translate("Decline selected bill items and provide feedback for revision")}. (
            {selectedBills.length})
          </DialogDescription>
        </DialogHeader>
        <div className='grid gap-6 py-4'>
          <Card>
            <CardHeader className='pb-3'>
              <CardTitle className='text-base'>{translate("Selected Bills Summary")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className='space-y-3'>
                <div className='flex items-center justify-between text-sm'>
                  <span className='text-muted-foreground'>{translate("Total Items")}:</span>
                  <Badge variant='secondary'>
                    {selectedBills?.length} {translate("items")}
                  </Badge>
                </div>
                <div className='flex items-center justify-between text-sm'>
                  <span className='text-muted-foreground'>{translate("Original Total")}:</span>
                  <span className='font-medium'>{formatCurrency(originalTotal, isHebrew)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className='space-y-4'>
            <div className='flex items-center space-x-2'>
              <Checkbox
                id='different-reasons'
                checked={useDifferentReasons}
                onCheckedChange={(checked) => setUseDifferentReasons(checked === true)}
              />
              <Label htmlFor='different-reasons' className='text-sm font-normal'>
                {translate("Provide different reasons for each item")}
              </Label>
            </div>
          </div>
          {!useDifferentReasons ? (
            <div className='grid gap-4'>
              <div className='grid gap-2'>
                <Label htmlFor='decline-reason'>{translate("Reason")}</Label>
                <Textarea
                  id='decline-reason'
                  placeholder={
                    translate(
                      "Provide specific details about why these bills are being declined and what needs to be corrected",
                    ) + "..."
                  }
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={5}
                />
                <p className='text-xs text-muted-foreground'>
                  {translate("This reason will be applied to all selected bills")}.
                </p>
              </div>
            </div>
          ) : (
            <div className='space-y-4'>
              <Alert>
                <AlertTriangle className='h-4 w-4' />
                <AlertTitle>{translate("Individual Reasons")}</AlertTitle>
                <AlertDescription>
                  {translate("You can provide different decline reasons for each bill item")}.
                </AlertDescription>
              </Alert>

              <div className='max-h-96 space-y-4 overflow-y-auto'>
                {declinedBillItems?.map((bill: DictType) => (
                  <Card key={bill.id}>
                    <CardHeader className='pb-3'>
                      <CardTitle className='flex gap-2 text-sm'>
                        {bill?._boq_item?.number} -
                        <span className='text-red-500'>
                          {formatCurrency(bill?.amount * bill?._boq_item?.discount_price, isHebrew)}
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-3'>
                      <div className='grid gap-2'>
                        <Label>Reason</Label>
                        <Textarea
                          placeholder='Specific reason for declining this bill...'
                          value={individualReasons[bill.id] || ""}
                          onChange={(e) => handleIndividualReasonChange(bill.id, e.target.value)}
                          rows={3}
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          <div className='space-y-4 overflow-auto'>
            <div className='rounded-lg border overflow-auto'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className='font-semibold text-black'>
                      {translate("Bill section")}
                    </TableHead>
                    <TableHead className='font-semibold text-black'>
                      {translate("Description")}
                    </TableHead>
                    <TableHead className='font-semibold text-black'>
                      {translate("Requested Amount")}
                    </TableHead>
                    <TableHead className='font-semibold text-black'>{translate("Sum")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {declinedBillItems?.map((bill: DictType) => {
                    return (
                      <TableRow key={bill.id}>
                        <TableCell className='font-semibold'>{bill?._boq_item?.number}</TableCell>
                        <TableCell className='max-w-[280px] truncate'>
                          {bill?._boq_item?.description}
                        </TableCell>
                        <TableCell>{bill?.amount}</TableCell>
                        <TableCell>
                          {formatCurrency(bill?.amount * bill?._boq_item?.discount_price, isHebrew)}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
        <DialogFooter className='gap-4'>
          <Button variant='outline' onClick={handleCancel}>
            {translate("Cancel")}
          </Button>
          <Button className='bg-error-600' onClick={handleDecline} disabled={!isValidDecline()}>
            <XCircle className='mr-1 h-4 w-4' />
            {translate("Decline")} {selectedBills.length}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
